/**
 * Dictionary source for the BROWSER (swapped in for `dictionary-source.ts` by
 * `clientOverrides` in vite.config.ts).
 *
 * English is the default locale and the fallback for every lookup, so it ships
 * in the entry chunk. German, French and Italian are separate chunks that
 * `prepareClientRuntime()` loads BEFORE hydration (src/client.tsx), so
 * `getDictionarySource` stays synchronous for React. Before this split every
 * visitor downloaded all four dictionaries (~350 KB of source) on every page.
 */
import type { Locale } from './config'
import type { Dictionary } from './dictionaries/de'
import { en } from './dictionaries/en'

const loaded: Partial<Record<Locale, Dictionary>> = { en }

const loaders: Record<Exclude<Locale, 'en'>, () => Promise<Dictionary>> = {
  de: () => import('./dictionaries/de').then((m) => m.de),
  fr: () => import('./dictionaries/fr').then((m) => m.fr),
  it: () => import('./dictionaries/it').then((m) => m.it),
}

export function getDictionarySource(locale: Locale): Dictionary {
  const dictionary = loaded[locale]
  if (dictionary) return dictionary
  // Only reachable if hydration started without `prepareClientRuntime()`.
  if (import.meta.env.DEV) {
    console.warn(`[i18n] dictionary "${locale}" used before it was loaded`)
  }
  return en
}

export async function loadDictionary(locale: Locale): Promise<void> {
  if (loaded[locale] || locale === 'en') return
  loaded[locale] = await loaders[locale]()
}
